import { DEPARTMENT_WORKSPACE_DEFAULTS } from '@/shared/permissions/workspaceAccess';
import { formatINR } from '@/shared/utils/currency';

/**
 * Departments for the Purchase Requisition dropdown.
 *
 * Derived from DEPARTMENT_WORKSPACE_DEFAULTS rather than hardcoded: that map is
 * the app's real department master (its keys mirror src/workspace-access.js on
 * the server and drive every workspace grant), so a department added there
 * appears here automatically instead of drifting out of a second list.
 *
 * `projects/constants.ts` has its own four-entry DEPARTMENT_OPTIONS, but that
 * one is scoped to the departments the Projects backend validates (DEPTS_VALID)
 * and is deliberately narrower than the company's actual department set — a
 * purchase requisition can legitimately come from Accounts or Management.
 *
 * NOTE: the backend field is deliberately NOT enum-constrained. Requisitions
 * already exist carrying free text such as 'Engineering', and an enum on the
 * schema would have rejected them. The constraint lives in this dropdown.
 */
const DEPARTMENT_LABELS: Record<string, string> = {
  sales: 'Sales',
  hr: 'HR',
  accounts: 'Accounts',
  it: 'IT',
  support: 'Support',
  customer_support: 'Customer Support',
  digital_marketing: 'Digital Marketing',
  management: 'Management',
};

function titleCase(key: string): string {
  return key.split('_').map(part => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

export const DEPARTMENT_OPTIONS: Array<{ value: string; label: string }> =
  Object.keys(DEPARTMENT_WORKSPACE_DEFAULTS).map(value => ({
    value,
    label: DEPARTMENT_LABELS[value] ?? titleCase(value),
  }));

/**
 * The three tiers the client spec (Purchase CRM audit) names for a Purchase
 * Request: Normal / Urgent / Critical. The backend enum on
 * PurchaseRequisitionModel (src/db-procurement.js) also still accepts the
 * older 'low'/'high' values, widened rather than replaced so a requisition
 * raised before this change stays valid — this dropdown is the actual
 * constraint the client asked for; it just doesn't offer those two anymore.
 */
export const PR_PRIORITY_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'urgent', label: 'Urgent' },
  { value: 'critical', label: 'Critical' },
] as const;

export const PR_PRIORITY_TONE: Record<string, string> = {
  low: 'bg-slate-100 text-slate-600',
  normal: 'bg-sky-100 text-sky-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-orange-100 text-orange-700',
  critical: 'bg-red-100 text-red-700',
};

/** Matches scoreBand() in src/shared/procurement/supplier-score.js. */
export const SCORE_BAND_TONE: Record<string, string> = {
  excellent: 'bg-emerald-100 text-emerald-700',
  good: 'bg-sky-100 text-sky-700',
  average: 'bg-amber-100 text-amber-700',
  poor: 'bg-red-100 text-red-700',
  unrated: 'bg-slate-100 text-slate-500',
};

/** Matches SUPPLIER_RATING_DIMENSIONS in src/shared/procurement/supplier-score.js. */
export const SUPPLIER_RATING_DIMENSIONS = [
  { key: 'deliveryTimeliness', label: 'Delivery timeliness' },
  { key: 'quality', label: 'Quality' },
  { key: 'priceCompetitiveness', label: 'Price competitiveness' },
  { key: 'responsiveness', label: 'Responsiveness' },
] as const;

export type SupplierRatingDimension = (typeof SUPPLIER_RATING_DIMENSIONS)[number]['key'];

/** Highest rating on the ladder — SUPPLIER_RATING_MAX in supplier-score.js. */
export const SUPPLIER_RATING_MAX = 5;

/** Matches RFQ_STATUSES in src/shared/procurement/rfq-comparison.js. */
export const RFQ_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'awarded', label: 'Awarded' },
  { value: 'cancelled', label: 'Cancelled' },
] as const;

export const RFQ_STATUS_TONE: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  sent: 'bg-sky-100 text-sky-700',
  quoted: 'bg-amber-100 text-amber-700',
  awarded: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-red-100 text-red-700',
};

/** Matches RFQ_QUOTE_STATUSES in src/shared/procurement/rfq-comparison.js. */
export const RFQ_QUOTE_STATUS_TONE: Record<string, string> = {
  pending: 'bg-slate-100 text-slate-500',
  received: 'bg-emerald-100 text-emerald-700',
  declined: 'bg-red-100 text-red-700',
};

// ── Money ────────────────────────────────────────────────────────────────────
//
// Every rate and total crossing this module's API boundary is in MINOR units
// (paise), because that is what the Procure-to-Pay schema stores. A buyer types
// and reads MAJOR units (rupees), so the conversion happens here — at the
// boundary — and nowhere else.
//
// Display delegates to formatINR (@/shared/utils/currency), the app's single
// INR display standard, rather than re-implementing the ₹ formatting a third
// time. ProcureToPayPage.tsx keeps its own local `fmt` helper: it is unchanged
// and still correct, and rewriting it was not part of this work.

/** Paise → a display string, e.g. 125000 → "₹1,250". */
export function formatMinor(minor: number | null | undefined): string {
  if (minor === null || minor === undefined || !Number.isFinite(Number(minor))) return '—';
  return formatINR(Number(minor) / 100);
}

/** Paise → rupees, for putting a stored rate into a number input. */
export function minorToMajor(minor: number | null | undefined): number {
  const n = Number(minor);
  return Number.isFinite(n) ? n / 100 : 0;
}

/**
 * Rupees → paise, for sending a typed rate to the server.
 * Rounded, so a value such as 12.345 cannot store a fractional paisa.
 */
export function majorToMinor(major: number | string | null | undefined): number {
  const n = Number(major);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}
